import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { RadialGauge } from "@/components/Gauge";
import { EmptyState } from "@/components/EmptyState";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { GitCompare, ArrowRight } from "lucide-react";

export default function Timeline() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState([]);
  const [compare, setCompare] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/timeline")
      .then((r) => setItems(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (id) =>
    setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id].slice(-3)));

  const runCompare = async () => {
    if (selected.length < 2) {
      toast.error(t("timeline.selectTwo"));
      return;
    }
    try {
      const r = await api.get(`/timeline/compare?ids=${selected.join(",")}`);
      setCompare(r.data);
    } catch {
      toast.error(t("timeline.compareFailed"));
    }
  };

  return (
    <Layout>
      <div className="mx-auto max-w-5xl px-6 py-12">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-3xl font-extrabold tracking-tight">
              {t("timeline.title")}
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {t("timeline.subtitle")}
            </p>
          </div>
          <Button
            onClick={runCompare}
            disabled={selected.length < 2}
            className="bg-foreground text-background hover:bg-foreground/90"
            data-testid="compare-btn"
          >
            <GitCompare className="mr-1.5 h-4 w-4" /> {t("timeline.compare", { count: selected.length })}
          </Button>
        </div>

        {loading ? (
          <p className="mt-12 text-sm text-muted-foreground">{t("common.loading")}</p>
        ) : items.length === 0 ? (
          <div className="mt-16">
            <EmptyState
              variant="timeline"
              testId="timeline-empty-state"
              title={t("timeline.emptyTitle")}
              description={t("timeline.emptyDesc")}
              actionLabel={t("timeline.startDecision")}
              onAction={() => nav("/")}
            />
          </div>
        ) : (
          <div className="mt-8 space-y-3" data-testid="timeline-list">
            {items.map((it, i) => (
              <motion.div
                key={it.decision_id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                className="flex items-center gap-4 rounded-lg border border-border bg-card p-4"
                data-testid="timeline-item"
              >
                <Checkbox
                  checked={selected.includes(it.decision_id)}
                  onCheckedChange={() => toggle(it.decision_id)}
                  data-testid={`timeline-check-${it.decision_id}`}
                />
                <button
                  onClick={() => nav(`/decision/${it.decision_id}`)}
                  className="min-w-0 flex-1 text-left"
                >
                  <p className="truncate text-sm font-semibold">{it.title}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    → {it.recommendation}
                  </p>
                </button>
                <div className="flex items-center gap-5 text-center">
                  <div>
                    <p className="font-mono text-lg font-bold text-ikb">{it.decision_score}</p>
                    <p className="text-[10px] uppercase tracking-overline text-muted-foreground">
                      {t("common.score")}
                    </p>
                  </div>
                  <div>
                    <p className="font-mono text-lg font-bold">{it.confidence}%</p>
                    <p className="text-[10px] uppercase tracking-overline text-muted-foreground">
                      {t("common.conf")}
                    </p>
                  </div>
                  <ArrowRight
                    className="h-4 w-4 cursor-pointer text-muted-foreground"
                    onClick={() => nav(`/decision/${it.decision_id}`)}
                  />
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {compare && (
          <div className="mt-12" data-testid="compare-result">
            <h2 className="font-display text-xl font-bold">{t("timeline.comparison")}</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {compare.map((c) => (
                <div key={c.id} className="card-crisp p-5">
                  <p className="text-sm font-semibold">{c.title}</p>
                  <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{c.query}</p>
                  {c.recommendation && (
                    <>
                      <div className="mt-4 flex justify-center gap-6">
                        <RadialGauge
                          value={c.recommendation.decision_score}
                          label="Score"
                          tone="score"
                        />
                        <RadialGauge
                          value={c.recommendation.confidence}
                          label="Conf"
                          tone="confidence"
                        />
                      </div>
                      <p className="mt-3 text-center text-sm font-semibold">
                        {c.recommendation.option_name}
                      </p>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

